import React, { useEffect, useState } from "react";
import { Box, CssBaseline, Grid } from "@mui/material";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import { useLocation, useNavigate } from "react-router-dom";
import {
  drawerWidth,
  GeneralAdminRoutes,
  mainLayoutStyles,
  showNotification,
} from "../../../../../constants";

import { useQuickTalesContext } from "../../../../../providers/OrganizationAdmin/QuickTalesProvider";
import { useAlbumsContext } from "../../../../../providers/OrganizationAdmin/AlbumProvider";

import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import CommonTable from "../../../../../common/CommonTable";
import TableSearchBar from "../../../../../common/TableSearchBar";
import QuickTalesTableRow from "../../../../../common/QuickTalesTableRow";
import {
  getAlbumbyId,
  getQuicktalesSharedWithUser,
  getSpecificUserAlbumShares,
  quickTalesSearch,
  updateAlbum,
} from "../../../../../modules/OrganizationAlbums/hooks/AlbumsHooks";
import {
  getSingleQuickTale,
  getSingleUser,
  getSpecificUserQuickTales,
} from "../../../../../modules/OrganizationQuickTales/hooks/QuickTalesHooks";

const AddQuickTalesToGAAlbumContainer = () => {
  const { loading, setLoading, GAQuickTales, handleGetGAQuickTales } =
    useQuickTalesContext();
  useAlbumsContext();

  const location = useLocation();
  const navigate = useNavigate();

  const [albumDetails, setAlbumDetails] = useState(location.state.albumDetails);
  const [selected, setSelected] = useState([]);
  const [quictalesInOrg, setQuictalesInOrg] = useState([]);
  const [allQuickTalesData, setAllQuickTalesData] = useState([]);
  const idsToRemove = albumDetails.quicktales; // IDs to remove from the quicktales

  useEffect(() => {
    setAlbumDetails(location.state.albumDetails);
    if (
      !location?.state?.albumDetails?.userData ||
      Object.keys(location?.state?.albumDetails?.userData).length === 0
    ) {
      handleGetGAQuickTales();
    }
  }, [location.state.albumDetails]);

  useEffect(() => {
    if (
      !location?.state?.albumDetails?.userData ||
      Object.keys(location?.state?.albumDetails?.userData).length === 0
    ) {
      setQuictalesInOrg(GAQuickTales);
      setAllQuickTalesData(GAQuickTales);
    }
  }, [GAQuickTales]);

  useEffect(() => {
    if (
      location?.state?.albumDetails?.userData != null ||
      location?.state?.albumDetails?.userData != {}
    ) {
      handleGetUserAlbums();
    }
  }, []);

  const handleGetUserAlbums = async () => {
    try {
      const temp = [];
      const userId = location?.state?.albumDetails?.user;

      // Fetch the shared albums, user quick tales, and shared quicktales in parallel
      const [docsSnap, userQTsdocsSnap, sharedQTsdocsSnap] = await Promise.all([
        getSpecificUserAlbumShares(userId),
        getSpecificUserQuickTales(userId),
        getQuicktalesSharedWithUser(userId),
      ]);

      // Convert QuerySnapshot to array of documents if necessary
      const docsArray = docsSnap.docs || docsSnap;

      // Helper function to process quick tales
      const processQuickTale = async (qtId) => {
        try {
          const userQTsdocsSnap = await getSingleQuickTale(qtId);
          const [userTemp, narratorTemp] = await Promise.all([
            getSingleUser(userQTsdocsSnap.data().createdBy),
            getSingleUser(userQTsdocsSnap.data().narrator),
          ]);

          return {
            id: userQTsdocsSnap.id,
            ...userQTsdocsSnap.data(),
            photoUrl:
              userQTsdocsSnap?.data().imageUrls?.[0] ||
              userQTsdocsSnap?.data().photoUrl ||
              "",
            createdByData: userTemp?.data() || null,
            narratorData: narratorTemp?.data() || null,
          };
        } catch (err) {
          console.error(`Error processing quick tale ID: ${qtId}`, err.message);
          return null; // Return null in case of an error to be filtered out later
        }
      };

      // Process shared albums and their quick tales
      const albumPromises = docsArray.map(async (item) => {
        try {
          const albumDocSnap = await getAlbumbyId(item.data().album);
          const qtsIdsTemp = albumDocSnap.data().quicktales || [];

          // Fetch all quick tales related to this album
          const quickTales = await Promise.all(
            qtsIdsTemp.map(async (qtId) => await processQuickTale(qtId))
          );

          return quickTales.filter(Boolean); // Filter out any null values
        } catch (err) {
          console.error(
            `Error processing album: ${item.data().album}`,
            err.message
          );
          return []; // Return empty array in case of an error
        }
      });

      const albumsData = (await Promise.all(albumPromises)).flat();

      // Process user quick tales
      const userQTsArray = userQTsdocsSnap.docs || userQTsdocsSnap;
      const userQuickTales = userQTsArray.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      // Process shared quick tales
      const sharedQTsArray = sharedQTsdocsSnap.docs || sharedQTsdocsSnap;
      const sharedQuickTales = await Promise.all(
        sharedQTsArray.map((item) => processQuickTale(item.data().quicktale))
      );

      // Combine all quick tales without duplicates
      const uniqueQuickTales = new Map();

      [
        ...albumsData,
        ...userQuickTales,
        ...sharedQuickTales.filter(Boolean),
      ].forEach((qt) => {
        if (!uniqueQuickTales.has(qt.id)) {
          uniqueQuickTales.set(qt.id, qt);
        }
      });

      const allQuickTalesTemp = Array.from(uniqueQuickTales.values());

      const filteredQuicktales = allQuickTalesTemp.filter(
        (quicktale) => !idsToRemove.includes(quicktale.id)
      );

      setQuictalesInOrg(filteredQuicktales);
      setAllQuickTalesData(filteredQuicktales);
      console.log("qt::", filteredQuicktales);

      return filteredQuicktales; // Return the data if needed elsewhere
    } catch (error) {
      console.error("Error getting user albums:", error.message);
      throw error; // Optionally rethrow to handle it in the calling function
    }
  };

  const handleSearchQuickTales = async (e) => {
    const filteredQuicktales = allQuickTalesData.filter(
      (quicktale) => !idsToRemove.includes(quicktale.id)
    );
    try {
      let value = e.target.value;
      if (value.length >= 1) {
        let quicktalesSearchTemp = await quickTalesSearch(
          filteredQuicktales,
          value
        );
        // console.log("quicktalesSearchTemp",quicktalesSearchTemp);
        setQuictalesInOrg(quicktalesSearchTemp);
      } else {
        setQuictalesInOrg(filteredQuicktales);
      }
    } catch (error) {
      console.log("error searching quicktales:", error.message);
    }
  };

  const handleSaveQuickTales = async () => {
    console.log("selectedID", selected);
    try {
      setLoading(true);
      const existingQuicktales = albumDetails.quicktales || [];
      const updatedQuicktales = [...selected, ...existingQuicktales];
      console.log("Merged QuickTales:", updatedQuicktales);
      let dataTemp = {
        quicktales: updatedQuicktales,
        privacy: "public",
      };
      const selectedQTs = allQuickTalesData
        .filter((qt) => selected.includes(qt.id))
        .map((qtData) => qtData);

      for (const qtData of selectedQTs) {
        if (qtData?.status === "private") {
          dataTemp.privacy = "private";
        }
      }

      // console.log(updateQuickTale);

      let responseTemp = await updateAlbum(albumDetails.id, dataTemp);
      setAlbumDetails((prevDetails) => ({
        ...prevDetails,
        quicktales: updatedQuicktales,
      }));

      // const sourceQuickTales = currentUser?.organization
      //   ? approvedQuickTales
      //   : organizationId
      //   ? approvedQuickTales
      //   : GAQuickTales;
      const allIdsToRemove = [...idsToRemove, ...selected];
      const filteredQuicktales = allQuickTalesData.filter(
        (quicktale) => !allIdsToRemove.includes(quicktale.id)
      );
      // Set the filtered quicktales to state
      setQuictalesInOrg(filteredQuicktales);
      // await handleGetAllAlbums();

      setSelected([]);
      navigate(GeneralAdminRoutes.albumDetails, {
        state: { albumDetails },
      });
      showNotification("Album QuickTales added successfully.");
      // Toast("success", "Album quicktales added successfully.");
    } catch (error) {
      console.log("error updating album quicktales:", error.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box sx={{ display: "flex", width: `calc(100% - ${drawerWidth}px)` }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={`Add QuickTales to ${albumDetails && albumDetails.title}`}
        bgColor="#fff"
        buttonText={"Edit"}
        submitButtonText={"Submit"}
        // handleGetIdQuickTales={handleGetIdQuickTales}
        selected={selected}
        setSelected={setSelected}
        editEnabled={true}
        cancelButton={true}
        loading={loading}
        onCancelClick={() => {
          navigate(GeneralAdminRoutes.albumDetails, {
            state: { albumDetails },
          });
        }}
        handleSave={handleSaveQuickTales}
      />

      <GeneralAdminSidebar />

      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          <TableSearchBar
            placeholder={"Search Title, Created By, Told By"}
            handleSearch={handleSearchQuickTales}
          />

          <CommonTable
            tableData={quictalesInOrg}
            selected={selected}
            setSelected={setSelected}
            CommonTableRow={QuickTalesTableRow}
            showPosition={false}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddQuickTalesToGAAlbumContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};
